import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast';

/**
 * Campos obligatorios de un formulario (los estilos están en index.css):
 * - el campo cuyo valor vive en el propio input/select se marca con `required` (y `min`/`step="any"` si el backend
 *   pide un mínimo); el que depende del estado (un buscador cuyo valor real es el id elegido) con `faltaSi(falta)`;
 * - mientras falta, el borde es ámbar; después de intentar guardar con faltantes (el contenedor queda con
 *   `data-intento-guardar`), rojo. Sólo dentro del contenedor (lleva `data-obligatorios`).
 *
 * `validar()` va primero en el guardado: si falta algo enfoca el primer campo, avisa y devuelve false.
 * `activo` es la ventana abierta: al cerrarse se borra la marca del intento.
 */

export const MENSAJE_OBLIGATORIOS = 'Completá los campos obligatorios';

type Campo = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/**
 * Atributos de un campo obligatorio cuyo valor real está en el estado (p. ej. el id del proveedor elegido).
 * `obligatorio` = false para uno que sólo a veces lo es: entonces no se marca.
 */
export const faltaSi = (falta: boolean, obligatorio = true) => ({
  'aria-required': obligatorio ? (true as const) : undefined,
  'data-falta': obligatorio && falta ? 'true' : undefined,
});

/** Si al campo le falta el valor (obligatorio vacío o marcado con `faltaSi`). Lo usa también la ayuda interactiva. */
export const campoFalta = (el: Campo) => {
  if (el.disabled || (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) && el.readOnly) return false;
  if (el.hasAttribute('data-falta')) return true;
  return el.required && !el.validity.valid;
};

/** Campos obligatorios sin completar dentro del contenedor, en el orden de la pantalla. */
export const camposFaltantes = (contenedor: ParentNode): Campo[] =>
  Array.from(contenedor.querySelectorAll<Campo>('input, select, textarea')).filter(campoFalta);

const nombreCampo = (el: Campo) =>
  (el.dataset.campo || el.getAttribute('aria-label') || ('placeholder' in el ? el.placeholder : '') || el.title || '').trim();

const MAX_NOMBRES = 4;
const mensajeFaltantes = (nombres: string[]) => {
  if (nombres.length === 0) return MENSAJE_OBLIGATORIOS;
  const lista = nombres.slice(0, MAX_NOMBRES).join(', ');
  return `${MENSAJE_OBLIGATORIOS}: ${lista}${nombres.length > MAX_NOMBRES ? '…' : ''}`;
};

export function useCamposObligatorios(activo: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [intento, setIntento] = useState(false);
  const toast = useToast();

  useEffect(() => { if (!activo) setIntento(false); }, [activo]);

  const validar = useCallback((): boolean => {
    const faltantes = ref.current ? camposFaltantes(ref.current) : [];
    if (faltantes.length === 0) { setIntento(false); return true; }
    setIntento(true);
    // Cada ítem se dibuja dos veces (fila de escritorio y tarjeta de celular): se enfoca el primero visible.
    (faltantes.find(el => el.getClientRects().length > 0) ?? faltantes[0]).focus();
    toast.addToast(mensajeFaltantes([...new Set(faltantes.map(nombreCampo).filter(Boolean))]), 'error');
    return false;
  }, [toast]);

  return {
    /**
     * Props del contenedor del formulario (el que envuelve todos sus campos). `data-obligatorios` activa los estilos de
     * index.css: fuera de un contenedor así (p. ej. el Login) un `required` no se pinta.
     */
    contenedor: { ref, 'data-obligatorios': '', 'data-intento-guardar': intento ? '' : undefined },
    validar,
  };
}
